import React, { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Sparkles, X, Download, Share2 } from 'lucide-react';
import { Button } from './ui/button';
import { isNative, nativeShare, hapticLight } from '../lib/nativeFeel';
import { useBackHandler } from '../lib/useBackHandler';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatDayName = (isoDate) => {
  const d = new Date(isoDate + 'T00:00:00');
  return DAY_NAMES[d.getDay()];
};

const formatDateRange = (start, end) => {
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  const sStr = s.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  const eStr = e.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${sStr} – ${eStr}`;
};

const ShareableGratitudeCard = ({ isOpen, onClose, weekData }) => {
  const cardRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [shareSupported] = useState(() => typeof navigator !== 'undefined' && !!navigator.share);

  // Android hardware Back should close this overlay before router history.
  useBackHandler(isOpen, onClose);

  const renderToCanvas = async () => {
    if (!cardRef.current) return null;
    return await html2canvas(cardRef.current, {
      backgroundColor: '#1C1C1E',
      scale: 2,
      useCORS: true,
      logging: false,
    });
  };

  const handleDownload = async () => {
    setExporting(true);
    try {
      const canvas = await renderToCanvas();
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = `futureself-gratitude-week-${weekData?.week_end || 'export'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      // silent fail
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    hapticLight();
    // On the web the Web Share API is enough; on iOS/Android WKWebView
    // navigator.share also exists and hands to the native sheet. If it
    // isn't available, we degrade gracefully:
    //   - native build: text-only share via Capacitor Share plugin
    //   - web build: PNG download.
    if (!shareSupported) {
      if (isNative()) {
        const ok = await nativeShare({
          title: 'My Week in Gratitude',
          text: 'Small practice. Lasting impact. — Future Self',
        });
        if (ok) return;
      }
      return handleDownload();
    }
    setExporting(true);
    try {
      const canvas = await renderToCanvas();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setExporting(false);
          return;
        }
        const file = new File([blob], `gratitude-week.png`, { type: 'image/png' });
        try {
          await navigator.share({
            files: [file],
            title: 'My Week in Gratitude',
            text: 'Small practice. Lasting impact.',
          });
        } catch (e) {
          // user cancelled or share failed; fall back to download
          handleDownload();
        } finally {
          setExporting(false);
        }
      }, 'image/png');
    } catch (e) {
      setExporting(false);
    }
  };

  if (!isOpen || !weekData) return null;

  // Take up to 7 days, prefer ones with entries; show day-by-day with most prominent first
  const orderedDays = weekData.days
    .slice()
    .reverse(); // most recent first

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in px-4 py-6 overflow-y-auto"
      data-testid="shareable-gratitude-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm mx-auto flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X */}
        <button
          onClick={onClose}
          className="absolute -top-2 right-0 text-[#B5B1AB] hover:text-white p-1 z-10"
          data-testid="close-shareable-card"
          aria-label="Close"
        >
          <X className="w-6 h-6" strokeWidth={1.5} />
        </button>

        {/* The exportable card (Instagram-story 9:16 inspired) */}
        <div
          ref={cardRef}
          data-testid="shareable-card-canvas"
          style={{
            width: '360px',
            background: 'linear-gradient(160deg, #1F1F22 0%, #252528 45%, #2A2520 100%)',
            borderRadius: '32px',
            padding: '36px 28px',
            border: '1px solid rgba(198, 167, 94, 0.25)',
            position: 'relative',
            overflow: 'hidden',
            color: '#D1C8BF',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          {/* Gold blob accent */}
          <div style={{
            position: 'absolute',
            top: '-60px',
            right: '-60px',
            width: '180px',
            height: '180px',
            background: 'rgba(198, 167, 94, 0.18)',
            borderRadius: '50%',
            filter: 'blur(40px)',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-80px',
            left: '-60px',
            width: '200px',
            height: '200px',
            background: 'rgba(139, 170, 158, 0.12)',
            borderRadius: '50%',
            filter: 'blur(50px)',
          }} />

          {/* Content */}
          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <p style={{
                color: '#C6A75E',
                fontSize: '10px',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                marginBottom: '8px',
                fontWeight: 500,
              }}>
                ✨ Week in Gratitude
              </p>
              <h2 style={{
                fontFamily: '"Cormorant Garamond", "Playfair Display", Georgia, serif',
                fontSize: '28px',
                color: '#D1C8BF',
                fontWeight: 500,
                margin: '4px 0 6px 0',
                lineHeight: 1.2,
              }}>
                Seven days of joy
              </h2>
              <p style={{
                color: '#A8A8A8',
                fontSize: '11px',
                fontStyle: 'italic',
                margin: 0,
              }}>
                {formatDateRange(weekData.week_start, weekData.week_end)}
              </p>
            </div>

            {/* Stats row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '8px',
              marginBottom: '20px',
              padding: '14px',
              background: 'rgba(28, 28, 30, 0.55)',
              borderRadius: '14px',
              border: '1px solid rgba(255, 255, 255, 0.04)',
            }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '24px',
                  color: '#D1C8BF',
                  margin: 0,
                  lineHeight: 1,
                }}>
                  {weekData.days_completed}
                  <span style={{ color: '#666666', fontSize: '14px' }}>/7</span>
                </p>
                <p style={{ color: '#8BAA9E', fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
                  Days
                </p>
              </div>
              <div style={{ width: '1px', background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '24px',
                  color: '#D1C8BF',
                  margin: 0,
                  lineHeight: 1,
                }}>
                  {weekData.total_entries}
                </p>
                <p style={{ color: '#C6A75E', fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
                  Entries
                </p>
              </div>
              <div style={{ width: '1px', background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ flex: 1, textAlign: 'center' }}>
                <p style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontSize: '24px',
                  color: '#D1C8BF',
                  margin: 0,
                  lineHeight: 1,
                }}>
                  {weekData.completion_rate}
                  <span style={{ color: '#666666', fontSize: '14px' }}>%</span>
                </p>
                <p style={{ color: '#8BAA9E', fontSize: '9px', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '4px 0 0 0' }}>
                  Rate
                </p>
              </div>
            </div>

            {/* Days */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              {orderedDays.map((day) => (
                <div
                  key={day.date}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    background: day.entry_count > 0
                      ? 'rgba(198, 167, 94, 0.06)'
                      : 'rgba(28, 28, 30, 0.3)',
                    borderRadius: '12px',
                    border: day.entry_count > 0
                      ? '1px solid rgba(198, 167, 94, 0.15)'
                      : '1px solid rgba(255, 255, 255, 0.03)',
                  }}
                >
                  <div style={{
                    width: '32px',
                    fontSize: '10px',
                    color: day.entry_count > 0 ? '#C6A75E' : '#666666',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}>
                    {formatDayName(day.date)}
                  </div>
                  <div style={{ flex: 1 }}>
                    {day.entry_count > 0 ? (
                      <p style={{
                        fontSize: '11px',
                        color: '#D1C8BF',
                        margin: 0,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {day.entries.slice(0, 2).join(' · ')}
                        {day.entry_count > 2 && ` · +${day.entry_count - 2} more`}
                      </p>
                    ) : (
                      <p style={{ fontSize: '10px', color: '#555', margin: 0, fontStyle: 'italic' }}>—</p>
                    )}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: day.entry_count > 0 ? '#8BAA9E' : '#555555',
                    fontWeight: 600,
                  }}>
                    {day.entry_count > 0 ? `${day.entry_count}` : ''}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer quote + brand */}
            <div style={{ textAlign: 'center' }}>
              <p style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontStyle: 'italic',
                color: '#C6A75E',
                fontSize: '14px',
                margin: '0 0 16px 0',
                lineHeight: 1.4,
              }}>
                &ldquo;Small practice. Lasting impact.&rdquo;
              </p>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 14px',
                background: 'rgba(139, 170, 158, 0.1)',
                borderRadius: '999px',
                border: '1px solid rgba(139, 170, 158, 0.2)',
              }}>
                <span style={{ color: '#8BAA9E', fontSize: '9px', letterSpacing: '0.2em', textTransform: 'uppercase', fontWeight: 500 }}>
                  FutureSelf
                </span>
              </div>
              <p style={{
                color: '#C6A75E',
                fontSize: '9px',
                letterSpacing: '0.15em',
                marginTop: '10px',
                marginBottom: 0,
                opacity: 0.7,
                fontWeight: 500,
              }}>
                app.futureself.es
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons (outside the captured card) */}
        <div className="flex gap-3 w-full px-2">
          <Button
            onClick={handleShare}
            disabled={exporting}
            className="flex-1 bg-[#C6A75E] hover:bg-[#D4B86E] text-[#1C1C1E] rounded-full py-3 font-medium flex items-center justify-center gap-2"
            data-testid="share-gratitude-card-btn"
          >
            {shareSupported ? (
              <>
                <Share2 className="w-4 h-4" strokeWidth={2} />
                {exporting ? 'Preparing...' : 'Share'}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" strokeWidth={2} />
                {exporting ? 'Saving...' : 'Save Image'}
              </>
            )}
          </Button>
          {shareSupported && (
            <Button
              onClick={handleDownload}
              disabled={exporting}
              variant="outline"
              className="flex-1 border-white/15 text-[#DCD3CA] hover:bg-white/5 rounded-full py-3 font-medium flex items-center justify-center gap-2"
              data-testid="download-gratitude-card-btn"
            >
              <Download className="w-4 h-4" strokeWidth={1.5} />
              Save
            </Button>
          )}
        </div>

        <p className="text-[#7A7670] text-xs font-sans text-center max-w-xs">
          <Sparkles className="w-3 h-3 inline mr-1" /> Share your week — inspire someone to start theirs.
        </p>
      </div>
    </div>
  );
};

export default ShareableGratitudeCard;
